import { getSession } from '@/domains/auth/services/auth.service';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface TrackingData {
  orderId: string;
  status: string;
  deliveryStatus: string;
  driverName: string;
  driverPhone: string;
  driverPhoto: string;
}

interface RealTimeTrackingProps {
  orderId: number;
  onStatusChange?: (status: string) => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

export default function RealTimeTracking({ orderId, onStatusChange }: RealTimeTrackingProps) {
  const [trackingData, setTrackingData] = useState<TrackingData>({
    orderId: String(orderId),
    status: 'loading',
    deliveryStatus: 'en_attente',
    driverName: 'Livreur non assigne',
    driverPhone: '',
    driverPhoto: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const getAuthToken = () => {
    const session = getSession();
    if (!session) {
      navigate('/login');
      return;
    }
    return session.token;
  };

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/commandes/${orderId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Échec du chargement des détails de la commande.');
      }

      const data = await response.json();
      const livraisons = Array.isArray(data.livraisons) ? data.livraisons : [];
      const lastLivraison = [...livraisons].sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      )[0];
      const driver = lastLivraison?.livreur;

      setTrackingData(prev => ({
        ...prev,
        orderId: data.code_suivi || String(data.id),
        status: data.statut,
        deliveryStatus: lastLivraison?.statut || 'en_attente',
        driverName: driver ? `${driver.prenom || ''} ${driver.nom || ''}`.trim() : 'Livreur non assigne',
        driverPhone: driver?.telephone || '',
        driverPhoto: driver?.photo_profil || '',
      }));
      if (onStatusChange) {
        onStatusChange(lastLivraison?.statut || data.statut);
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des détails de la commande:', err.message);
      setError(err.message || 'Une erreur est survenue lors du chargement des détails de la commande.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
    // Optional: set up polling for status updates if needed, but not real-time map
    const interval = setInterval(fetchOrderDetails, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [orderId]);

  const [notifications, setNotifications] = useState<Array<{
    id: string;
    message: string;
    time: string;
    type: 'info' | 'success' | 'warning';
  }>>([]);

  const addNotification = (message: string, type: 'info' | 'success' | 'warning') => {
    const notification = {
      id: Date.now().toString(),
      message,
      time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      type
    };

    setNotifications(prev => [notification, ...prev.slice(0, 4)]);

    // Notification push simulée
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('ONAPILA - Mise à jour de livraison', {
        body: message,
        icon: '/favicon.ico'
      });
    }
  };

  const requestNotificationPermission = () => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'en_attente':
      case 'pending':
        return {
          text: 'En attente',
          color: 'bg-blue-500',
          icon: 'ri-time-line',
          description: 'Votre commande est en attente d\'affectation à un livreur.'
        };
      case 'acceptee':
        return {
          text: 'Commande acceptée',
          color: 'bg-cyan-500',
          icon: 'ri-checkbox-circle-line',
          description: 'Votre commande a été acceptée et un livreur vous a été assigné.'
        };
      case 'recuperee':
        return {
          text: 'Colis récupéré',
          color: 'bg-blue-500',
          icon: 'ri-inbox-archive-line',
          description: 'Le livreur a récupéré votre colis.'
        };
      case 'en_cours':
        return {
          text: 'En préparation',
          color: 'bg-yellow-500',
          icon: 'ri-time-line',
          description: 'Votre commande est en cours de préparation.'
        };
      case 'en_route':
      case 'en_transit':
        return {
          text: 'En livraison',
          color: 'bg-orange-500',
          icon: 'ri-truck-line',
          description: 'Le livreur est en route vers votre adresse.'
        };
      case 'livree':
        return {
          text: 'Livrée',
          color: 'bg-green-500',
          icon: 'ri-check-double-line',
          description: 'Votre commande a été livrée avec succès.'
        };
      default:
        return {
          text: 'Statut inconnu',
          color: 'bg-gray-500',
          icon: 'ri-question-line',
          description: 'Statut de la commande non défini.'
        };
    }
  };

  const statusInfo = getStatusInfo(trackingData.deliveryStatus || trackingData.status);

  const getProgress = (deliveryStatus: string) => {
    if (deliveryStatus === 'en_attente' || deliveryStatus === 'pending') return 0;
    if (deliveryStatus === 'acceptee') return 25;
    if (deliveryStatus === 'recuperee') return 50;
    if (deliveryStatus === 'en_transit' || deliveryStatus === 'en_route') return 75;
    if (deliveryStatus === 'livree') return 100;
    return 0;
  };

  const progress = getProgress(trackingData.deliveryStatus || trackingData.status);

  // URL Google Maps pour le suivi (non temps réel)
  const mapUrl = `https://www.google.com/maps/embed?pb=!1m28!1m12!1m3!1d15955.234567890123!2d-4.0267!3d5.3364!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!4m13!3e0!4m5!1s0x0%3A0x0!2m2!1d-4.0267!2d5.3364!4m5!1s0x0%3A0x0!2m2!1d-3.9583!2d5.36!5e0!3m2!1sfr!2sci!4v1640995200000!5m2!1sfr!2sci`;

  return (
    <div className="space-y-6">
      {/* Demande de permission pour les notifications */}
      {Notification.permission === 'default' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <i className="ri-notification-3-line text-blue-600 text-xl"></i>
              <div>
                <p className="font-medium text-blue-900">Activer les notifications</p>
                <p className="text-sm text-blue-700">Recevez des alertes en temps réel sur l'état de votre livraison</p>
              </div>
            </div>
            <button
              onClick={requestNotificationPermission}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap"
            >
              Activer
            </button>
          </div>
        </div>
      )}

      {/* Statut principal */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Chargement du suivi de commande...</p>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative" role="alert">
            <strong className="font-bold">Erreur:</strong>
            <span className="block sm:inline ml-2">{error}</span>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 ${statusInfo.color} rounded-lg flex items-center justify-center`}>
                  <i className={`${statusInfo.icon} text-white text-xl`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{statusInfo.text}</h3>
                  <p className="text-gray-600">{statusInfo.description}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-teal-600">N/A min</div>
                <div className="text-sm text-gray-600">Temps estimé</div>
              </div>
            </div>

            {/* Progression */}
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Progression de la livraison</span>
                <span className="font-medium text-gray-900">
                  {progress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${statusInfo.color}`}
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>En attente</span>
                <span>Acceptée</span>
                <span>Collectée</span>
                <span>Transit</span>
                <span>Livrée</span>
              </div>
            </div>

            {/* Informations livreur */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {trackingData.driverPhoto ? (
                    <img src={trackingData.driverPhoto} alt="Livreur" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="ri-user-line text-teal-600"></i>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900">{trackingData.driverName || 'Livreur non assigne'}</p>
                    <p className="text-sm text-gray-600">{trackingData.driverPhone || 'Telephone non disponible'}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button disabled={!trackingData.driverPhone} className="p-2 bg-teal-600 text-white rounded-lg disabled:bg-gray-300 disabled:text-gray-500">
                    <i className="ri-phone-line"></i>
                  </button>
                  <button disabled className="p-2 bg-gray-300 text-gray-500 rounded-lg">
                    <i className="ri-message-3-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Carte interactive */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white p-4">
          <h3 className="text-lg font-semibold">Position non disponible</h3>
          <p className="text-teal-100 text-sm">Le suivi en temps réel de la position n'est pas activé.</p>
        </div>

        <div className="relative">
          <iframe
            src={mapUrl}
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full"
            title="Carte statique"
          ></iframe>

          {/* Overlay avec informations */}
          <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
              <div>
                <p className="font-medium text-sm">Carte statique</p>
                <p className="text-xs text-gray-600">Position du livreur non mise à jour</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications récentes */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Notifications récentes</h3>

        {notifications.length === 0 ? (
          <div className="text-center py-8">
            <i className="ri-notification-off-line text-4xl text-gray-300 mb-2"></i>
            <p className="text-gray-500">Aucune notification pour le moment</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-3 rounded-lg border-l-4 ${notification.type === 'success' ? 'bg-green-50 border-green-500' :
                    notification.type === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                      'bg-blue-50 border-blue-500'
                  }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <i className={`${notification.type === 'success' ? 'ri-check-line text-green-600' :
                        notification.type === 'warning' ? 'ri-alert-line text-yellow-600' :
                          'ri-information-line text-blue-600'
                      } text-lg mt-0.5`}></i>
                    <div>
                      <p className="font-medium text-gray-900">{notification.message}</p>
                      <p className="text-sm text-gray-600">{notification.time}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
